import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner, Alert } from "react-bootstrap";
import { FaSyncAlt, FaTruck, FaCheckCircle } from "react-icons/fa";
import { apiFetch } from "./utils/auth";
import "./pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

const fmtMoney = (v) =>
  v == null ? "—" : Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" });
const fmtDate = (s) => { if (!s) return "—"; const d = new Date(s); return isNaN(d) ? "—" : d.toLocaleDateString(); };

// =============================================================================
// MyShipments — the dispatcher's work queue (SQL/048, action 00080).
// One screen: the orders assigned to me today. "Ship →" opens the existing
// Generate & Ship wizard; a successful ship auto-completes the row (the
// wizard calls /api/dispatch/complete-by-order), and "Mark completed" covers
// anything handled outside the wizard. A completed row whose order is still
// open in Epicor is flagged short-shipped — it will be back in tomorrow's
// upload for the remainder.
// =============================================================================
export default function MyShipments() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);       // null = loading
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await apiFetch(`${API_URL}/api/dispatch/my`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) throw new Error(d.error || `Couldn't load your queue (HTTP ${r.status}).`);
      setRows(d.rows || []);
    } catch (e) { setError(e.message); setRows([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markCompleted = async (row) => {
    if (!window.confirm(`Mark order #${row.orderNum} as completed?`)) return;
    setBusyId(row.id);
    try {
      const r = await apiFetch(`${API_URL}/api/dispatch/${row.id}/complete`, { method: "POST" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) throw new Error(d.error || `Couldn't complete (HTTP ${r.status}).`);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusyId(null); }
  };

  const open = (rows || []).filter((r) => r.status !== "Completed");
  const done = (rows || []).filter((r) => r.status === "Completed");

  const Card = ({ row }) => {
    const completed = row.status === "Completed";
    const shortShipped = completed && row.orderOpen === true;
    return (
      <div className="qw-card-flat qw-pad" style={{ marginBottom: 10, opacity: completed ? 0.75 : 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700, color: "#0F172A" }}>Order #{row.orderNum}</span>
              {completed
                ? <span className="qw-pill qw-pill-emerald">COMPLETED</span>
                : <span className="qw-pill qw-pill-indigo">ASSIGNED</span>}
              {shortShipped && <span className="qw-pill qw-pill-amber">ORDER STILL OPEN — SHORT SHIPPED</span>}
              {row.shippedStatus === "partial" && !completed && <span className="qw-pill qw-pill-amber">PARTIALLY SHIPPED</span>}
              {row.reassignedFrom && <span className="qw-pill qw-pill-slate">from {row.reassignedFrom}</span>}
            </div>
            <div style={{ fontSize: 13, color: "#475569", marginTop: 4 }}>
              {row.customerName || "—"}{row.customerCustID ? ` · ${row.customerCustID}` : ""}
            </div>
            <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
              PO {row.poNum || "—"} · Ordered {fmtDate(row.orderDate)} · {fmtMoney(row.orderAmt)}
              {completed && row.packNum ? ` · Pack #${row.packNum}` : ""}
              {completed && row.completedAt ? ` · Done ${fmtDate(row.completedAt)}` : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            {!completed && (
              <>
                <button className="qw-btn qw-btn-primary qw-btn-sm"
                  onClick={() => navigate(`/order/${encodeURIComponent(row.orderNum)}/ship`)}>
                  <FaTruck style={{ marginRight: 6 }} /> Ship →
                </button>
                <button className="qw-btn qw-btn-ghost qw-btn-sm" disabled={busyId === row.id}
                  title="Use only when this order was handled outside the wizard — shipping normally completes it automatically."
                  onClick={() => markCompleted(row)}>
                  {busyId === row.id ? <Spinner animation="border" size="sm" /> : <><FaCheckCircle style={{ marginRight: 4 }} /> Mark completed</>}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="qw-root content-halo" style={{ maxWidth: 900, margin: "24px auto", padding: "28px 24px 36px", borderRadius: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div className="qw-eyebrow">Dispatch queue</div>
          <h2 className="qw-h2" style={{ margin: 0 }}>My Shipments</h2>
        </div>
        <button className="qw-btn qw-btn-outline qw-btn-sm" onClick={() => { setRows(null); load(); }}>
          <FaSyncAlt style={{ marginRight: 6 }} /> Refresh
        </button>
      </div>
      <p className="qw-sub" style={{ marginTop: 6, fontSize: 13 }}>
        The orders assigned to you today. Ship what's available — if stock is short, ship the rest anyway;
        the order stays open and comes back on a later day's list for the remainder.
      </p>

      {error && <Alert variant="danger" style={{ fontSize: 13 }}>{error}</Alert>}

      {rows == null ? (
        <div style={{ textAlign: "center", padding: 30, color: "#64748B" }}>
          <Spinner animation="border" size="sm" /> Loading your queue…
        </div>
      ) : rows.length === 0 ? (
        <div className="qw-card-flat qw-pad" style={{ textAlign: "center", color: "#94A3B8", fontSize: 14 }}>
          Nothing assigned to you today. 🎉
        </div>
      ) : (
        <>
          <div className="qw-eyebrow" style={{ margin: "14px 0 8px" }}>To ship ({open.length})</div>
          {open.length === 0
            ? <div style={{ fontSize: 13, color: "#94A3B8", marginBottom: 10 }}>All done — every assignment is completed.</div>
            : open.map((row) => <Card key={row.id} row={row} />)}
          {done.length > 0 && (
            <>
              <div className="qw-eyebrow" style={{ margin: "18px 0 8px" }}>Completed ({done.length})</div>
              {done.map((row) => <Card key={row.id} row={row} />)}
            </>
          )}
        </>
      )}
    </div>
  );
}
